import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "blockSize", "children", "descriptiveLabel", "easing", "hasLabel", "inlineSize", "max", "maxBlockSize", "maxInlineSize", "min", "minBlockSize", "minInlineSize", "style", "use", "value", "valueLabel"];
import unescapedText from 'I18n/utils/unescapedText';
import I18n from 'I18n';
import { useMemo } from 'react';
import styled from 'foundations-theming/utils';
import memoizeOne from 'react-utils/memoizeOne';
import { uniqueId } from '../../utils/uniqueId';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { Flex } from '../../../components/Flex/Flex';
import { Text } from '../../../components/Text/Text';
import { View } from '../../../components/View/View';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PROGRESS_WRAPPER_HEIGHT = '16px';
const ProgressWrapper = styled.div.withConfig({
  displayName: "Progress__ProgressWrapper",
  componentId: "sc-1bzebe0-0"
})(({
  $use,
  $sizeCssStyles,
  theme
}) => {
  return Object.assign({
    backgroundColor: $use === 'danger' ? theme.color['fill-alert-default'] : theme.color['fill-accent-neutral-subtle-alt'],
    borderRadius: theme.borderRadius['100'],
    height: PROGRESS_WRAPPER_HEIGHT,
    overflow: 'hidden'
  }, $sizeCssStyles);
});
const ProgressBar = styled.div.withConfig({
  displayName: "Progress__ProgressBar",
  componentId: "sc-1bzebe0-1"
})(({
  $use,
  easing,
  $value,
  theme
}) => {
  return {
    backgroundImage: `linear-gradient(73deg, ${$use === 'success' ? theme.color['fill-positive-default'] : theme.color['fill-accent-light-orange-default']} 8.26%, ${$use === 'success' ? theme.color['fill-info-default'] : theme.color['fill-accent-yellow-default']} 92.52%)`,
    transition: `width 0.6s ${easing}`,
    borderRadius: $use !== 'danger' ? theme.borderRadius['100'] : '',
    borderRight: $use === 'danger' && $value === 0 || $use === 'danger' && $value === 100 ? '' : $use === 'danger' ? `3px ${theme.color['text-core-on-fill-default']} solid` : '',
    color: theme.color['text-core-on-fill-default'],
    fontSize: theme.typography['detail-100'].fontSize,
    height: '100%',
    lineHeight: PROGRESS_WRAPPER_HEIGHT,
    // Line height needs to match the wrapper's height which is hard-coded
    textAlign: 'center'
  };
});
export const Progress = props => {
  const {
      'aria-label': ariaLabel,
      blockSize,
      children,
      descriptiveLabel,
      easing = 'ease',
      hasLabel = false,
      inlineSize,
      max = 100,
      maxBlockSize,
      maxInlineSize,
      min = 0,
      minBlockSize,
      minInlineSize,
      style,
      use = 'success',
      value = 0,
      valueLabel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const rawPercentage = Math.min((value - min) / (max - min), 1);
  const formattedPercentage = I18n.formatPercentage(rawPercentage * 100);
  const labelId = useMemo(() => `Progress-label-${uniqueId()}`, []);
  const renderedLabel = useMemo(() => {
    if (children != null) {
      return children;
    }
    if (hasLabel) {
      return /*#__PURE__*/_jsx("span", {
        children: formattedPercentage
      });
    }
    return /*#__PURE__*/_jsx(ScreenReaderOnly, {
      children: unescapedText('ui.progress.percentComplete', {
        percent: formattedPercentage
      })
    });
  }, [children, hasLabel, formattedPercentage]);
  const ariaObject = useMemo(() => {
    const ariaAttributes = {};
    if (descriptiveLabel) {
      ariaAttributes['aria-labelledby'] = labelId;
    } else if (ariaLabel) {
      ariaAttributes['aria-label'] = ariaLabel;
    } else {
      console.warn('Progress: Use a descriptiveLabel prop when using this component to meet accessibility standards. If one is not needed in your design, use an aria-label to indicate the content of the Progress instead.');
    }
    return ariaAttributes;
  }, [descriptiveLabel, ariaLabel, labelId]);
  const stackedLabel = useMemo(() => {
    if (valueLabel != null || descriptiveLabel != null) {
      return /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        marginBlockEnd: "200",
        children: [/*#__PURE__*/_jsx(Text, {
          id: labelId,
          typography: "body-200",
          children: descriptiveLabel
        }), /*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: valueLabel
        })]
      });
    }
    return null;
  }, [valueLabel, descriptiveLabel, labelId]);
  const getStyle = memoizeOne((baseStyle, percentage) => Object.assign({}, baseStyle, {
    width: `${percentage * 100}%`
  }));
  const sizeStyleProps = {
    blockSize,
    inlineSize,
    maxBlockSize,
    maxInlineSize,
    minBlockSize,
    minInlineSize
  };
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const spaceTokenProps = tokenProps;
  return /*#__PURE__*/_jsxs(View, Object.assign({
    marginBlockEnd: "400"
  }, callbackProps, spaceTokenProps, {
    children: [stackedLabel, /*#__PURE__*/_jsx(ProgressWrapper, Object.assign({
      $sizeCssStyles: sizeStyleProps,
      $use: use
    }, domProps, {
      children: /*#__PURE__*/_jsx(ProgressBar, Object.assign({
        "data-value": value,
        "aria-valuemax": max,
        "aria-valuemin": min,
        "aria-valuenow": value,
        "aria-live": "polite",
        easing: easing,
        role: "progressbar",
        style: getStyle(style, rawPercentage),
        $use: use,
        $value: value
      }, ariaObject, {
        children: renderedLabel
      }))
    }))]
  }));
};